import { useState } from "react";
import { Autocomplete, Badge, Box, Button, Card, Grid, Group, RingProgress, Skeleton, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { IconSparkles } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { fmtNumber, type GlycemicEstimation } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { GOAL_PRESETS, GoalRatingResult } from "../components/GoalRating";

const giBand = (gi: number) => (gi < 56 ? ["Low", "lime.8"] : gi < 70 ? ["Medium", "yellow.7"] : ["High", "red.6"]) as [string, string];
const glBand = (gl: number) => (gl < 11 ? ["Low", "lime.8"] : gl < 20 ? ["Medium", "yellow.7"] : ["High", "red.6"]) as [string, string];
const mid = (a?: number, b?: number) => ((a ?? 0) + (b ?? a ?? 0)) / 2;

function RangeBar({ low, high, max, color }: { low?: number; high?: number; max: number; color: string }) {
  const l = Math.max(0, Math.min(100, ((low ?? 0) / max) * 100));
  const h = Math.max(l + 2, Math.min(100, ((high ?? low ?? 0) / max) * 100));
  return (
    <Box h={8} style={{ background: "var(--pd-sand)", borderRadius: 99, position: "relative" }}>
      <Box style={{ position: "absolute", left: `${l}%`, width: `${h - l}%`, top: 0, bottom: 0, borderRadius: 99, background: `var(--mantine-color-${color.replace(".", "-")})` }} />
    </Box>
  );
}

function GlycemicTable({ rows }: { rows: GlycemicEstimation[] }) {
  return (
    <Table verticalSpacing="sm">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Ingredient</Table.Th>
          <Table.Th w="32%">Glycemic index</Table.Th>
          <Table.Th w="28%">Glycemic load</Table.Th>
          <Table.Th ta="right">Confidence</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map((r, i) => {
          const [giL, giC] = giBand(mid(r.gi_low, r.gi_high));
          const [glL, glC] = glBand(mid(r.gl_low, r.gl_high));
          return (
            <Table.Tr key={i}>
              <Table.Td fw={600}>{r.name}</Table.Td>
              <Table.Td>
                <Group gap={6} mb={4}>
                  <Text fz="sm" ff="monospace">
                    {fmtNumber(r.gi_low)}–{fmtNumber(r.gi_high)}
                  </Text>
                  <Badge size="xs" color={giC} variant="light">
                    {giL}
                  </Badge>
                </Group>
                <RangeBar low={r.gi_low} high={r.gi_high} max={110} color={giC} />
              </Table.Td>
              <Table.Td>
                <Group gap={6} mb={4}>
                  <Text fz="sm" ff="monospace">
                    {fmtNumber(r.gl_low, 1)}–{fmtNumber(r.gl_high, 1)}
                  </Text>
                  <Badge size="xs" color={glC} variant="light">
                    {glL}
                  </Badge>
                </Group>
                <RangeBar low={r.gl_low} high={r.gl_high} max={40} color={glC} />
              </Table.Td>
              <Table.Td ta="right" ff="monospace">
                {r.confidence !== undefined ? `${Math.round((r.confidence <= 1 ? r.confidence * 100 : r.confidence))}%` : "–"}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

export function InsightsPage() {
  const [text, setText] = useState("");
  const gi = useMutation({ mutationFn: (t: string) => passio.tools.estimateGlycemicIndexLoad(t) });
  const upf = useMutation({ mutationFn: (t: string) => passio.tools.ultraProcessedFoodRating(t) });
  const [goal, setGoal] = useState(GOAL_PRESETS[0]);
  const rate = useMutation({ mutationFn: (v: { food: string; goal: string }) => passio.tools.genericFoodRating(v.food, v.goal) });
  const run = (t = text) => {
    if (!t.trim()) return;
    setText(t);
    gi.mutate(t);
    upf.mutate(t);
    if (goal.trim()) rate.mutate({ food: t, goal: goal.trim() });
    else rate.reset();
  };
  const rating = upf.data?.data.rating;
  const ratingColor = rating === undefined ? "gray" : rating <= 3 ? "lime" : rating <= 6 ? "yellow" : "red";

  return (
    <RequireFeature feature="insights">
      <PageHeader
        eyebrow="POST /napi/tools/estimateGlycemicIndexLoad · /ultraProcessedFoodRating · /genericFoodRating"
        title="Food insights"
        description="Glycemic index and load ranges per ingredient, a 1-10 ultra-processing score with the reasoning behind it, and a 0-10 rating against any goal you write."
        actions={<RouteModelPicker routes={["estimateGlycemicIndexLoad", "ultraProcessedFoodRating", "genericFoodRating"]} />}
      />
      <Card mb="lg">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            run();
          }}
        >
          <Group gap="sm" wrap="nowrap">
            <TextInput style={{ flex: 1 }} size="md" placeholder="A food, product or meal: e.g. white rice with teriyaki chicken" value={text} onChange={(e) => setText(e.currentTarget.value)} />
            <Button type="submit" size="md" leftSection={<IconSparkles size={16} />} loading={gi.isPending || upf.isPending || rate.isPending}>
              Analyze
            </Button>
          </Group>
          <Autocomplete
            mt="sm"
            label="Also rate it for a goal (optional)"
            description="genericFoodRating takes any criterion you write. Leave empty to skip."
            data={GOAL_PRESETS}
            value={goal}
            onChange={setGoal}
          />
          <Group gap={6} mt="sm">
            {["white rice with teriyaki chicken", "Doritos Nacho Cheese", "steel cut oats with blueberries", "glazed donut and orange juice"].map((ex) => (
              <Badge key={ex} variant="outline" color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => run(ex)}>
                {ex}
              </Badge>
            ))}
          </Group>
        </form>
      </Card>

      {!gi.data && !upf.data && !gi.isPending && !gi.error && <EmptyState title="Describe a food or meal">The tools run in parallel; each is a separate call in the inspector.</EmptyState>}

      {rate.error ? <ErrorNotice error={rate.error} /> : null}
      {rate.isPending && <Skeleton h={140} radius="lg" mb="lg" />}
      {rate.data && !rate.isPending && (
        <Card mb="lg">
          <ResultHeader title="Rated for your goal" meta={rate.data.meta} feedback={<FeedbackButtons requestId={rate.data.meta.requestId} compact />} />
          <GoalRatingResult data={rate.data.data} criterion={rate.variables?.goal} />
        </Card>
      )}

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          {gi.error ? <ErrorNotice error={gi.error} /> : null}
          {gi.isPending && <Skeleton h={240} radius="lg" />}
          {gi.data && !gi.isPending && (
            <Card>
              <ResultHeader title="Glycemic index & load" meta={gi.data.meta} feedback={<FeedbackButtons requestId={gi.data.meta.requestId} compact />} />
              {gi.data.data.ingredients?.length ? <GlycemicTable rows={gi.data.data.ingredients} /> : <Text c="dimmed">No estimates returned.</Text>}
              <Text fz="xs" c="dimmed" mt="sm">
                GI bands: low &lt; 56, medium 56-69, high ≥ 70. GL bands: low &lt; 11, medium 11-19, high ≥ 20.
              </Text>
            </Card>
          )}
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          {upf.error ? <ErrorNotice error={upf.error} /> : null}
          {upf.isPending && <Skeleton h={240} radius="lg" />}
          {upf.data && !upf.isPending && (
            <Card>
              <ResultHeader title="Ultra-processed rating" meta={upf.data.meta} />
              {upf.data.data.isSupplement ? (
                <Badge color="violet" variant="light" size="lg" tt="none">
                  Dietary supplement · not rated
                </Badge>
              ) : (
              <Stack align="center" gap={4}>
                <RingProgress
                  size={150}
                  thickness={14}
                  roundCaps
                  sections={[{ value: ((rating ?? 0) / 10) * 100, color: ratingColor }]}
                  label={
                    <Stack gap={0} align="center">
                      <Text fz={34} fw={800} lh={1}>
                        {rating ?? "–"}
                      </Text>
                      <Text fz="xs" c="dimmed">
                        of 10
                      </Text>
                    </Stack>
                  }
                />
                <Text fz="sm" c="dimmed">
                  1 = minimally processed · 10 = ultra-processed
                </Text>
              </Stack>
              )}
              {upf.data.data.highlightedIngredients?.length ? (
                <Stack gap={4} mt="md">
                  <Title order={4} fz="sm">
                    Driving the score
                  </Title>
                  <Group gap={6}>
                    {upf.data.data.highlightedIngredients.map((h) => (
                      <Badge key={h} color="orange" variant="light" tt="none">
                        {h}
                      </Badge>
                    ))}
                  </Group>
                </Stack>
              ) : null}
              {upf.data.data.chainOfThought && (
                <Text fz="sm" mt="md" style={{ whiteSpace: "pre-wrap" }}>
                  {upf.data.data.chainOfThought}
                </Text>
              )}
              <Box mt="sm">
                <FeedbackButtons requestId={upf.data.meta.requestId} compact />
              </Box>
            </Card>
          )}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
